/** Client-side protocol for Answer or Bluff room sync over WebSocket (host + guests). */

/** Keep-alive interval so proxies / mobile OS do not drop the socket while the tab is idle or backgrounded. */
export const ANSWER_BLUFF_WS_PING_INTERVAL_MS = 25_000;

export function stringifyBluffWsPing(): string {
  return JSON.stringify({ type: "ping", ts: Date.now() });
}

export const ANSWER_BLUFF_MSG = {
  state: "answer_bluff_state",
  vote: "answer_bluff_vote",
} as const;

export type AnswerBluffGuestPhase = "idle" | "question" | "answer" | "vote";

export interface AnswerBluffStateMessage {
  type: typeof ANSWER_BLUFF_MSG.state;
  phase: AnswerBluffGuestPhase;
  round: number;
  question?: string;
  answer?: string;
}

/** Server expects `type: "join"` for presence (joined_room / left_room). */
export interface AnswerBluffJoinMessage {
  type: "join";
  display_name: string;
}

export interface AnswerBluffVoteMessage {
  type: typeof ANSWER_BLUFF_MSG.vote;
  round: number;
  verdict: "truth" | "bluff";
}

export function parseAnswerBluffIncoming(raw: string): AnswerBluffStateMessage | null {
  try {
    const o = JSON.parse(raw) as unknown;
    if (!o || typeof o !== "object") return null;
    const rec = o as Record<string, unknown>;
    if (rec.type !== ANSWER_BLUFF_MSG.state) return null;
    const phase = rec.phase as AnswerBluffGuestPhase;
    if (phase !== "idle" && phase !== "question" && phase !== "answer" && phase !== "vote") return null;
    const round = typeof rec.round === "number" ? rec.round : Number(rec.round) || 0;
    return {
      type: ANSWER_BLUFF_MSG.state,
      phase,
      round,
      question: typeof rec.question === "string" ? rec.question : undefined,
      answer: typeof rec.answer === "string" ? rec.answer : undefined,
    };
  } catch {
    return null;
  }
}

export function stringifyState(msg: AnswerBluffStateMessage): string {
  return JSON.stringify(msg);
}

export function stringifyJoin(displayName: string): string {
  const msg: AnswerBluffJoinMessage = {
    type: "join",
    display_name: displayName.trim(),
  };
  return JSON.stringify(msg);
}

export function stringifyVote(round: number, verdict: "truth" | "bluff"): string {
  const msg: AnswerBluffVoteMessage = {
    type: ANSWER_BLUFF_MSG.vote,
    round,
    verdict,
  };
  return JSON.stringify(msg);
}
